<template>
  <div class="document-checklist-container">
    <Stack gap="4">
      <!-- Loading Spinner -->
      <div v-if="state.loading.checklist || state.loading.template" class="tw-flex tw-justify-center tw-py-8">
        <LoaderWithText>Loading document checklist...</LoaderWithText>
      </div>

      <!-- Error Message -->
      <Callout v-if="state.error" status="danger" iconBefore="fas fa-exclamation-circle" class="tw-mb-4">
        {{ state.error }}
      </Callout>

      <!-- Main Content When Loaded -->
      <Stack v-if="!state.loading.checklist && !state.loading.template && !state.error && state.checklist" gap="4">

        <!-- Header -->
        <div class="header stk-py-4 stk-px-6">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <div class="tw-flex-1">
              <Title size="4" class="tw-mb-1">{{ entityName }}</Title>
              <Subtitle class="tw-mb-0 tw-text-slate-600">Document Checklist</Subtitle>
            </div>
            
            <!-- Only show menu when template is assigned -->
            <div v-if="state.currentTemplateId" class="tw-flex tw-items-center tw-gap-3">
              <!-- Template Selection Dropdown -->
              <Dropdown 
                :label="state.currentTemplateName ? 'Change Template' : 'Select Template'"
                anchor="right"
                :disabled="isReadOnly"
                class="template-dropdown"
              >
                <div class="tw-p-3 tw-min-w-64">
                  <div class="tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-2">Select Checklist Template</div>
                  <div class="tw-text-xs tw-text-slate-500 tw-mb-3">Choose a different checklist template as the base for this client</div>
                  
                  <div v-if="state.loading.availableChecklists" class="tw-text-center tw-py-2">
                    <LoaderWithText size="sm">Loading...</LoaderWithText>
                  </div>
                  
                  <div v-else class="tw-space-y-1">
                    <DropdownItem
                      v-for="checklist in state.availableChecklistsForEntity"
                      :key="checklist.id"
                      @click="selectTemplate(checklist.id)"
                      class="dropdown-template-item"
                      :class="{ 'dropdown-template-selected': checklist.id === state.currentTemplateId }"
                    >
                      <div class="tw-flex tw-items-center tw-justify-between tw-w-full">
                        <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                          <!-- Selection indicator for dropdown -->
                          <div class="dropdown-selection-indicator">
                            <div class="dropdown-selection-circle" :class="{ 'selected': checklist.id === state.currentTemplateId }">
                              <svg v-if="checklist.id === state.currentTemplateId" width="10" height="10" viewBox="0 0 24 24" class="dropdown-check-icon">
                                <path fill="currentColor" d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                              </svg>
                            </div>
                          </div>
                          
                          <div>
                            <div class="tw-font-medium tw-text-sm">{{ checklist.name }}</div>
                            <div class="tw-text-xs tw-text-slate-500">
                              {{ checklist.is_default_for_advisor ? 'Your default template' : 'Custom template' }}
                            </div>
                          </div>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-1">
                          <Badge v-if="checklist.is_default_for_advisor" status="info" class="tw-text-xs">Default</Badge>
                        </div>
                      </div>
                    </DropdownItem>
                  </div>
                </div>
              </Dropdown>
              
              <!-- Clear All Button -->
              <Button
                v-if="!isReadOnly"
                @click="clearAllDocuments"
                variant="subtle"
                size="3"
              >
                Clear All
              </Button>
            </div>
          </div>

          <!-- Current Template Info -->
          <div v-if="state.currentTemplateName" class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-600">
            <Badge status="default" class="tw-text-xs">
              Template: {{ state.currentTemplateName }}
            </Badge>
            <span v-if="state.hasCustomizations" class="tw-text-blue-600 tw-text-xs">
              • Changes made
            </span>
          </div>
        </div>

        <!-- Setup Mode: No Active Checklist -->
        <Callout v-if="!state.currentTemplateId && !state.isInSetupMode" status="warning" iconBefore="fas fa-exclamation-triangle">
          <div class="tw-flex tw-justify-between tw-items-start">
            <div>
              <strong>No Checklist Template Selected</strong>
              <p class="tw-mt-1 tw-mb-0 tw-text-sm">
                This client doesn't have a checklist template selected yet. Choose a template to start collecting documents.
              </p>
            </div>
            <Button
              @click="enterSetupMode"
              variant="primary"
              size="3"
            >
              Select Template
            </Button>
          </div>
        </Callout>

        <!-- Setup Form Content -->
        <div v-if="state.isInSetupMode" class="setup-form-container">
          <div v-if="state.loading.assigning" class="tw-text-center tw-py-6">
            <LoaderWithText>Loading available templates...</LoaderWithText>
          </div>

          <Stack v-if="!state.loading.assigning" gap="4">
            <div class="tw-text-center">
              <Title size="5" class="tw-mb-2">Select a Checklist Template</Title>
              <p class="tw-text-sm tw-text-slate-500 tw-mb-4">
                Choose a template to use as the foundation for {{ entityName }}'s document checklist.
              </p>
            </div>

            <!-- Template Selection Grid -->
            <div v-if="state.availableChecklistsForEntity.length > 0" class="template-selection-container">
              <Stack gap="3">
                <div 
                  v-for="template in state.availableChecklistsForEntity"
                  :key="template.id"
                  class="template-option-card"
                  :class="{ 'template-selected': state.selectedTemplateForSetup === template.id }"
                  @click="state.selectedTemplateForSetup = template.id"
                  @keydown.enter="state.selectedTemplateForSetup = template.id"
                  @keydown.space.prevent="state.selectedTemplateForSetup = template.id"
                  role="button"
                  tabindex="0"
                  :aria-pressed="state.selectedTemplateForSetup === template.id"
                  :aria-label="`Select ${template.name} template`"
                >
                  <!-- Selection Indicator -->
                  <div class="selection-indicator">
                    <div class="selection-circle" :class="{ 'selected': state.selectedTemplateForSetup === template.id }">
                      <svg v-if="state.selectedTemplateForSetup === template.id" width="12" height="12" viewBox="0 0 24 24" class="check-icon">
                        <path fill="currentColor" d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                      </svg>
                    </div>
                  </div>

                  <!-- Template Content -->
                  <div class="template-content">
                    <div class="tw-flex tw-items-start tw-justify-between tw-mb-2">
                      <div class="tw-flex-1">
                        <h4 class="tw-font-semibold tw-text-slate-900 tw-mb-1">{{ template.name }}</h4>
                        <p class="tw-text-sm tw-text-slate-600 tw-mb-2">
                          {{ template.is_default_for_advisor ? 'Your default template' : 'Custom template' }}
                        </p>
                        <div class="tw-text-xs tw-text-slate-500">
                          Created {{ formatDate(template.created_at) }}
                        </div>
                      </div>
                      <Badge v-if="template.is_default_for_advisor" status="info" class="tw-text-xs">Default</Badge>
                    </div>
                  </div>

                  <!-- Hover Overlay -->
                  <div class="hover-overlay">
                    <Icon icon="fas fa-check" class="hover-icon" />
                  </div>
                </div>
              </Stack>
            </div>

            <div class="tw-flex tw-justify-center tw-gap-3">
              <Button
                @click="exitSetupMode"
                variant="subtle"
                size="3"
              >
                Cancel
              </Button>
              <Button
                @click="applySelectedTemplate"
                variant="primary"
                size="3"
                :disabled="!state.selectedTemplateForSetup"
                :loading="state.loading.assigning"
              >
                Use This Template
              </Button>
            </div>
          </Stack>
        </div>

        <!-- Main Checklist Content -->
        <div v-if="state.currentTemplateId && !state.isInSetupMode" class="checklist-content">
          <Stack gap="3">
            <div
              v-for="(section, index) in state.sections"
              :key="section.key"
              class="section-item"
            >
              <!-- Section Header -->
              <div class="section-header">
                <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                  <!-- Include/Exclude toggle -->
                  <Toggle
                    v-if="!isReadOnly"
                    :modelValue="section.is_included"
                    @update:modelValue="(value) => updateSectionIncluded(section, value)"
                    size="sm"
                    :disabled="isSystemSection(section)"  
                  />
                  <div class="tw-flex-1">
                    <div class="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                      <Badge v-if="isSystemSection(section)" status="default" class="tw-text-xs">Locked</Badge>
                      <Title size="6" class="tw-mb-0">{{ section.display_name || section.name }}</Title>
                      <Badge v-if="section.documents?.length > 0" status="info" class="tw-text-xs">
                        {{ section.documents.length }}
                      </Badge>
                    </div>
                    <p v-if="section.description" class="tw-text-sm tw-text-slate-600 tw-mb-0">
                      {{ section.description }}
                    </p>
                  </div>
                </div>
                
                <div class="tw-flex tw-items-center tw-gap-2">
                  <Button
                    @click="toggleSection(section.key)"
                    variant="ghost"
                    size="2"
                    :iconBefore="isExpanded(section) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                  />
                </div>
              </div>

              <!-- Section Content -->
              <div v-if="shouldShowSectionContent(section)" class="section-content" :class="{ 'section-disabled': !section.is_included }">
                
                <!-- Documents List -->
                <Stack v-if="section.documents && section.documents.length > 0" gap="2" class="tw-mb-3">
                  <div
                    v-for="(document, docIndex) in section.documents"
                    :key="document.id || docIndex"
                    class="document-item"
                    :class="{ 'document-disabled': !section.is_included }"
                  >
                    <!-- Document Icon -->
                    <div class="document-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" class="tw-text-blue-600">
                        <path fill="currentColor" d="M13,9V3.5L18.5,9M6,2C4.89,2 4,2.89 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2H6Z"/>
                      </svg>
                    </div>

                    <!-- Document Content -->
                    <div class="tw-flex-1 tw-min-w-0">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <span class="tw-font-medium tw-text-slate-900">
                          {{ document.name || document.document_type_name || document.custom_document_name }}
                        </span>
                        <Badge v-if="document.is_custom" status="info" class="tw-text-xs">Custom</Badge>
                      </div>
                      <div v-if="document.description" class="tw-text-xs tw-text-slate-600 tw-mt-1">
                        {{ document.description }}
                      </div>
                    </div>

                    <!-- Required Toggle -->
                    <div v-if="!isReadOnly && section.is_included" class="tw-flex tw-items-center tw-gap-2">
                      <span class="tw-text-xs tw-text-slate-600">Required</span>
                      <Toggle
                        :modelValue="document.required"
                        @update:modelValue="(value) => updateDocumentRequired(document, value)"
                        size="sm"
                      />
                    </div>

                    <!-- Remove Button -->
                    <Button
                      v-if="!isReadOnly"
                      @click="removeDocument(section, docIndex)"
                      variant="ghost"
                      size="2"
                      iconOnly="fas fa-times"
                      class="tw-text-slate-400 hover:tw-text-red-600"
                    />
                  </div>
                </Stack>

                <!-- Add Document -->
                <div v-if="!isReadOnly && section.is_included" class="add-document-area">
                  <DocumentTypeTypeahead 
                    :callback="(docType) => addDocumentFromTypeahead(section, docType)"
                    placeholder="Add document (search existing types or create custom)..."
                    :size="'is-small'"
                  />
                </div>

                <!-- Empty Section State -->
                <div v-if="section.is_included && (!section.documents || section.documents.length === 0)" 
                     class="empty-section-state">
                  <div class="tw-text-center tw-py-4">
                    <div class="tw-text-slate-500 tw-text-sm tw-mb-2">This section has no documents yet</div>
                    <!-- Document suggestions temporarily disabled due to bugs
                    <div v-if="!isReadOnly" class="tw-flex tw-flex-wrap tw-gap-1 tw-justify-center">
                      <Button
                        v-for="suggestion in getDocumentSuggestions(section.key).slice(0, 3)"
                        :key="suggestion"
                        @click="addDocumentFromTypeahead(section, { display_name: suggestion, is_custom: true })"
                        variant="outline"
                        size="1"
                        class="tw-text-xs"
                      >
                        {{ suggestion }}
                      </Button>
                    </div>
                    -->
                  </div>
                </div>
              </div>
            </div>
          </Stack>
        </div>

        <!-- Footer Actions -->
        <div v-if="state.currentTemplateId && !state.isInSetupMode && !isReadOnly" class="footer-actions">
          <div class="tw-flex tw-items-center tw-justify-between">
            <div class="tw-text-sm tw-text-slate-600">
              <span v-if="state.autoSaveStatus === 'saving'" class="tw-text-blue-600">
                <i class="fas fa-spinner fa-spin tw-mr-1"></i>
                Saving...
              </span>
              <span v-else-if="state.autoSaveStatus === 'saved'" class="tw-text-green-600">
                <i class="fas fa-check tw-mr-1"></i>
                Saved
              </span>
              <span v-else>
                Auto-save enabled
              </span>
            </div>
            
            <!--
            TODO: template editor ui/ux
             <div class="tw-flex tw-gap-2">
              <Button
                @click="saveAsTemplate"
                variant="secondary"
                size="3"
                :loading="state.loading.savingTemplate"
              >
                Save as Template
              </Button>
            </div> -->
          </div>
        </div>
      </Stack>

      <!-- Empty State -->
      <div v-if="!state.loading.checklist && !state.loading.template && !state.error && !state.checklist" class="empty-state">
        <svg xmlns="http://www.w3.org/2000/svg" class="tw-h-16 tw-w-16 tw-text-slate-300 tw-mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2H9M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p class="tw-text-slate-700 tw-font-medium tw-mb-2">No document checklist information found for this client.</p>
        <p class="tw-text-slate-500 tw-mb-6">This could mean no dossier template is assigned, or there was an issue loading data.</p>
      </div>
    </Stack>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick, onUnmounted, watch } from 'vue';
import { Title, Button, Badge, Toggle, Label, Callout, LoaderWithText, Dropdown, DropdownItem, Subtitle, Stack, Icon } from '@stakeui';
import DocumentTypeTypeahead from '@frontend/components/DocumentTypeTypeahead.vue';
import * as Sentry from '@sentry/vue';

export default {
  name: 'DocumentChecklist2',
  components: {
    Title,
    Button,
    Badge,
    Toggle,
    Label,
    Callout,
    LoaderWithText,
    DocumentTypeTypeahead,
    Dropdown,
    DropdownItem,
    Subtitle,
    Stack,
    Icon,
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    entityType: {
      type: String,
      default: 'client',
    },
    entityName: {
      type: String,
      default: null,
    },
    isReadOnly: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    // State - simplified with single source of truth for sections
    const state = reactive({
      checklist: null, // Basic checklist info for the client
      currentTemplateId: null, // Currently selected template ID
      currentTemplateName: null, // Currently selected template name
      sections: [], // Direct reactive sections array - single source of truth
      
      error: null,
      loading: {
        checklist: false,
        template: false,
        availableChecklists: false,
        assigning: false,
        savingTemplate: false,
        sendingToClient: false,
      },
      
      // Setup mode
      isInSetupMode: false,
      selectedTemplateForSetup: null,
      
      // Available templates for selection
      availableChecklistsForEntity: [],
      
      // Auto-save status
      autoSaveStatus: null, // null, 'saving', 'saved'
      hasUnsavedChanges: false,
      
      // NEW: Track if checklist has been customized from template
      hasCustomizations: false,
    });

    const expandedSections = ref([]);
    let autoSaveTimeout = null;

    const isSystemSection = (section) => {
        const key = (section?.key || '').toLowerCase();
        return key === 'other' || section?.is_system === true; // supports backend flag too
    };

    // Computed properties
    const entityName = computed(() => {
      if (props.entityName) return props.entityName;
      if (state.checklist?.entity_name) return state.checklist.entity_name;
      return 'Client';
    });

    // Remove the complex computed property - sections are now directly in state

    // Helper functions
    const isExpanded = (section) => {
      return expandedSections.value.includes(section.key);
    };

    const shouldShowSectionContent = (section) => {
      return section.is_included || expandedSections.value.includes(section.key);
    };

    // Extract client documents from the checklist data that's already been fetched
    function extractClientDocumentsFromChecklistData() {
      console.log('Extracting client documents from checklist data:', state.checklist);
      
      if (!state.checklist || !state.checklist.sections) {
        console.log('No checklist or sections data available');
        return [];
      }
      
      const clientDocuments = [];
      
      // Convert the sections data from get_checklist_completion to the format expected by buildSections
      Object.entries(state.checklist.sections).forEach(([sectionKey, sectionData]) => {
        console.log(`Processing section ${sectionKey}:`, sectionData);
        if (sectionData.items && Array.isArray(sectionData.items)) {
          sectionData.items.forEach(item => {
            const clientDoc = {
              id: item.id,
              section_key: sectionKey,
              document_type_id: item.document_type_id,
              document_type_name: item.document_type, // Extract the actual document type name
              custom_document_name: item.custom_document_name,
              required: item.is_required || false,
              status: item.status
            };
            console.log('Adding client document:', clientDoc);
            clientDocuments.push(clientDoc);
          });
        }
      });
      
      console.log('Extracted client documents:', clientDocuments);
      return clientDocuments;
    }

    // Build sections from template and client data - called once when data loads
    async function buildSections(templateDefinition, clientDocuments) {
      console.log('=== BUILDING SECTIONS ===');
      console.log('Template definition:', templateDefinition);
      console.log('Client documents:', clientDocuments);
      
      if (!templateDefinition) return [];
      
      const sectionArray = [];
      
      Object.entries(templateDefinition).forEach(([sectionKey, sectionData]) => {
        console.log(`\n--- Processing template section: ${sectionKey} ---`);
        console.log('Section data:', sectionData);
        
        const documents = [];
        
        // Add standard document types
        if (sectionData.document_type_pks && Array.isArray(sectionData.document_type_pks)) {
          console.log('Processing standard document types:', sectionData.document_type_pks);
          sectionData.document_type_pks.forEach((docTypeId, index) => {
            const clientCustomization = clientDocuments.find(cd => 
              cd.document_type_id === docTypeId && cd.section_key === sectionKey
            );
            
            console.log(`DocType ${docTypeId} - Client customization:`, clientCustomization);
            
            // Skip items that have been marked as not applicable
            if (clientCustomization?.status === 'not_applicable') {
              console.log(`Skipping docType ${docTypeId} - marked as not_applicable`);
              return;
            }
            
            const doc = {
              id: `standard-${docTypeId}`,
              document_type_id: docTypeId,
              name: clientCustomization?.document_type_name || `Document Type ${docTypeId}`,
              is_custom: false,
              required: clientCustomization?.required ?? (sectionData.document_types_required?.[docTypeId.toString()] || false),
              order: index,
              clientItemId: clientCustomization?.id,
              status: clientCustomization?.status || 'pending'
            };
            console.log('Adding standard document:', doc);
            documents.push(doc);
          });
        }
        
        // Add custom document types from template
        if (sectionData.custom_document_types && Array.isArray(sectionData.custom_document_types)) {
          console.log('Processing custom template document types:', sectionData.custom_document_types);
          sectionData.custom_document_types.forEach((customDocName, index) => {
            const clientCustomization = clientDocuments.find(cd => 
              cd.custom_document_name === customDocName && cd.section_key === sectionKey
            );
            
            console.log(`Custom template doc "${customDocName}" - Client customization:`, clientCustomization);
            
            // Skip items that have been marked as not applicable
            if (clientCustomization?.status === 'not_applicable') {
              console.log(`Skipping custom template doc "${customDocName}" - marked as not_applicable`);
              return;
            }
            
            const doc = {
              id: `template-custom-${sectionKey}-${index}`,
              document_type_id: null,
              name: customDocName,
              is_custom: true,
              required: clientCustomization?.required ?? (sectionData.custom_document_types_required?.[index.toString()] || false),
              order: (sectionData.document_type_pks?.length || 0) + index,
              clientItemId: clientCustomization?.id,
              fromTemplate: true,
              status: clientCustomization?.status || 'pending'
            };
            console.log('Adding custom template document:', doc);
            documents.push(doc);
          });
        }
        
        // Add client-only custom documents
        const clientOnlyDocs = clientDocuments.filter(cd => 
          cd.section_key === sectionKey && !cd.document_type_id && cd.custom_document_name &&
          !sectionData.custom_document_types?.includes(cd.custom_document_name) &&
          cd.status !== 'not_applicable' // Also filter out not_applicable client-only docs
        );
        
        console.log(`Client-only docs for section ${sectionKey}:`, clientOnlyDocs);
        
        clientOnlyDocs.forEach((clientDoc, index) => {
          const doc = {
            id: `client-custom-${clientDoc.id}`,
            document_type_id: null,
            name: clientDoc.custom_document_name,
            is_custom: true,
            required: clientDoc.required || false,
            order: 1000 + index,
            clientItemId: clientDoc.id,
            clientOnly: true,
            status: clientDoc.status || 'pending'
          };
          console.log('Adding client-only document:', doc);
          documents.push(doc);
        });
        
        // Add orphaned standard document types (client documents with document_type_id not in template)
        const templateDocTypeIds = sectionData.document_type_pks || [];
        const orphanedStandardDocs = clientDocuments.filter(cd => 
          cd.section_key === sectionKey && cd.document_type_id && 
          !templateDocTypeIds.includes(cd.document_type_id) &&
          cd.status !== 'not_applicable'
        );
        
        console.log(`Orphaned standard docs for section ${sectionKey}:`, orphanedStandardDocs);
        
        orphanedStandardDocs.forEach((clientDoc, index) => {
          const doc = {
            id: `orphaned-standard-${clientDoc.id}`,
            document_type_id: clientDoc.document_type_id,
            name: clientDoc.document_type_name || `Document Type ${clientDoc.document_type_id}`,
            is_custom: false,
            required: clientDoc.required || false,
            order: 2000 + index, // Place after client-only docs
            clientItemId: clientDoc.id,
            orphaned: true, // Mark as orphaned for reference
            status: clientDoc.status || 'pending'
          };
          console.log('Adding orphaned standard document:', doc);
          documents.push(doc);
        });
        
        documents.sort((a, b) => a.order - b.order);
        
        console.log(`Final documents for section ${sectionKey}:`, documents);
        
        sectionArray.push({
          key: sectionKey,
          display_name: sectionData.display_name || sectionKey.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
          description: sectionData.description || '',
          is_included: sectionData.is_included_in_checklist !== false,
          documents: documents
        });
      });
      
      console.log('=== FINAL SECTION ARRAY ===');
      console.log(sectionArray);
      
      // Check for unprocessed client documents
      const processedClientDocIds = new Set();
      sectionArray.forEach(section => {
        section.documents.forEach(doc => {
          if (doc.clientItemId) {
            processedClientDocIds.add(doc.clientItemId);
          }
        });
      });
      
      const unprocessedDocs = clientDocuments.filter(cd => !processedClientDocIds.has(cd.id));
      if (unprocessedDocs.length > 0) {
        console.warn('=== UNPROCESSED CLIENT DOCUMENTS ===');
        console.warn('These client documents were not added to any section:');
        unprocessedDocs.forEach(doc => {
          console.warn(`ID: ${doc.id}, Section: ${doc.section_key}, Name: ${doc.custom_document_name || 'Document Type ' + doc.document_type_id}, Status: ${doc.status}`);
        });
      }
      
      // After building sections, check if there are any customizations
      const hasAnyCustomizations = sectionArray.some(section => {
        return section.documents.some(doc => 
          doc.clientOnly || doc.orphaned || doc.clientItemId
        );
      });
      
      state.hasCustomizations = hasAnyCustomizations;
      
      state.sections = sectionArray;
    }

    const toggleSection = (sectionKey) => {
      const index = expandedSections.value.indexOf(sectionKey);
      if (index > -1) {
        expandedSections.value.splice(index, 1);
      } else {
        expandedSections.value.push(sectionKey);
      }
    };

    // Data loading methods
    async function fetchClientChecklist() {
      state.loading.checklist = true;
      state.error = null;
      
      try {
        const response = await fetch(`/api/v1/client-portal/checklists/${props.entityExternalId}/`, {
          headers: getHeaders()
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch client checklist: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Fetched client checklist:', data);

        state.checklist = data;
        
        if (data.checklist?.id) {
          state.currentTemplateId = data.checklist.id;
          state.currentTemplateName = data.checklist.name;
          await fetchTemplateDefinition(data.checklist.id);
        }
        
        await loadAvailableChecklists();
        
      } catch (error) {
        state.error = `Error fetching client checklist: ${error.message}`;
        Sentry.captureException(error)
        console.error(error);
      } finally {
        state.loading.checklist = false;
      }
    }

    async function fetchTemplateDefinition(templateId) {
      if (!templateId) return;
      
      state.loading.template = true;
      
      try {
        const response = await fetch(`/api/v1/checklists/${templateId}/`, {
          headers: getHeaders()
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch template definition: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Fetched template definition:', data);
        
        const templateDefinition = data.definition || {};
        
        // Extract client documents from the already-fetched checklist data
        const clientDocuments = extractClientDocumentsFromChecklistData();
        
        // Build sections from both datasets
        await buildSections(templateDefinition, clientDocuments);
        
        // Auto-expand sections with documents
        expandedSections.value = state.sections.filter(section => {
          return section.is_included && section.documents && section.documents.length > 0;
        }).map(section => section.key);
        
      } catch (error) {
        console.error('Error fetching template definition:', error);
        Sentry.captureException(error)
        state.sections = [];
      } finally {
        state.loading.template = false;
      }
    }

    async function loadAvailableChecklists() {
      if (state.availableChecklistsForEntity.length > 0) return;
      
      state.loading.availableChecklists = true;
      
      try {
        const response = await fetch(`/api/v1/client-portal/checklists/available/${props.entityExternalId}/`, {
          headers: getHeaders()
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch available checklists: ${response.status}`);
        }
        
        const data = await response.json();
        state.availableChecklistsForEntity = data.checklists || [];
      } catch (err) {
        console.error('Error loading available checklists:', err);
        Sentry.captureException(error)
        state.availableChecklistsForEntity = [];
      } finally {
        state.loading.availableChecklists = false;
      }
    }

    // Template selection methods
    async function selectTemplate(templateId) {
      if (templateId === state.currentTemplateId) return;
      
      state.loading.assigning = true;
      state.error = null;

      try {
        const response = await fetch(`/api/v1/client-portal/checklists/${props.entityExternalId}/set-active/`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ checklist_id: templateId })
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(`Failed to assign template: ${response.status} - ${errData.error || 'Server error'}`);
        }

        // Refresh everything
        await fetchClientChecklist();
        
      } catch (err) {
        state.error = `Error selecting template: ${err.message}`;
        console.error(err);
      } finally {
        state.loading.assigning = false;
      }
    }

    // Setup mode methods
    async function enterSetupMode() {
      state.isInSetupMode = true;
      state.selectedTemplateForSetup = null;
      await loadAvailableChecklists();
    }

    function exitSetupMode() {
      state.isInSetupMode = false;
    }

    async function applySelectedTemplate() {
      if (!state.selectedTemplateForSetup) return;
      
      state.loading.assigning = true;
      
      try {
        await selectTemplate(state.selectedTemplateForSetup);
        state.isInSetupMode = false;
      } catch (error) {
        // Error already handled in selectTemplate
      } finally {
        state.loading.assigning = false;
      }
    }

    // Document management methods
    function updateSectionIncluded(section, value) {
      if (isSystemSection(section)) return; // locked section
      // Directly update the section in our reactive state
      section.is_included = value;
      triggerAutoSave();
    }

    function updateDocumentRequired(document, value) {
      document.required = value;
      triggerAutoSave();
    }

    function addDocumentFromTypeahead(section, documentType) {
      const newDocument = {
        id: `new-${Date.now()}`,
        name: documentType.display_name,
        is_custom: documentType.is_custom || false,
        required: false,
        document_type_id: documentType.is_custom ? null : documentType.id,
        order: (section.documents?.length || 0),
        clientOnly: true, // Mark as client-only addition
        _isNewlyAdded: true // Flag to ensure this gets created on save
      };
      
      section.documents.push(newDocument);
      
      // Auto-expand section
      if (!expandedSections.value.includes(section.key)) {
        expandedSections.value.push(section.key);
      }
      
      triggerAutoSave();
    }

    function removeDocument(section, docIndex) {
      const document = section.documents[docIndex];
      
      if (document.clientItemId) {
        // Mark for deletion but keep in UI until server confirms
        document._markedForDeletion = true;
      } else {
        // Remove immediately if it's a new document
        section.documents.splice(docIndex, 1);
      }
      
      triggerAutoSave();
    }

    function clearAllDocuments() {
      if (confirm('Are you sure you want to clear all documents for this client? This will reset to the template defaults.')) {
        // Mark all client documents for deletion by iterating through sections
        state.sections.forEach(section => {
          section.documents.forEach(doc => {
            if (doc.clientItemId) {
              doc._markedForDeletion = true;
            }
          });
        });
        
        triggerAutoSave();
      }
    }

    // Auto-save functionality
    function triggerAutoSave() {
      state.hasUnsavedChanges = true;
      state.hasCustomizations = true; // Set this when any change is made
      
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
      
      autoSaveTimeout = setTimeout(async () => {
        await performAutoSave();
      }, 1000);
    }

    async function performAutoSave() {
      if (!state.hasUnsavedChanges) return;
      
      state.autoSaveStatus = 'saving';
      
      try {
        await saveClientCustomizations();
        state.autoSaveStatus = 'saved';
        state.hasUnsavedChanges = false;
        
        // Clear saved status after 3 seconds
        setTimeout(() => {
          if (state.autoSaveStatus === 'saved') {
            state.autoSaveStatus = null;
          }
        }, 3000);
        
      } catch (error) {
        Sentry.captureException(error)
        state.autoSaveStatus = null;
        console.error('Auto-save failed:', error);
      }
    }

    async function saveClientCustomizations() {
      const operations = [];
      
      console.log('Building operations from sections:', state.sections);
      
      // Process each section to build operations
      state.sections.forEach(section => {
        section.documents.forEach((document, index) => {
          if (document._markedForDeletion && document.clientItemId) {
            operations.push({
              action: 'delete',
              item_id: document.clientItemId
            });
          } else if ((document.clientOnly && !document.clientItemId) || document._isNewlyAdded) {
            const operation = {
              action: 'create',
              section_key: section.key,
              is_required: document.required || false
            };
            
            if (document.document_type_id) {
              operation.document_type_id = document.document_type_id;
            } else if (document.name) {
              operation.custom_document_name = document.name;
            }
            
            operations.push(operation);
          } else if (document.clientItemId) {
            operations.push({
              action: 'update',
              item_id: document.clientItemId,
              is_required: document.required
            });
          }
        });
      });
      
      if (operations.length === 0) {
        console.log('No operations to perform');
        return;
      }
      
      console.log('Saving client customizations:', operations);
      
      const response = await fetch('/api/v1/client-portal/checklist-items/bulk-update/', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          entity_id: props.entityExternalId,
          operations: operations
        })
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(`Failed to save: ${response.status} - ${errData.error || 'Server error'}`);
      }
      
      // Get the response data to update IDs for newly created items
      const responseData = await response.json();
      
      // Update the local state with server responses instead of full refresh
      await updateLocalStateFromServerResponse(responseData, operations);
      
      // Clear the _isNewlyAdded flags after successful save
      state.sections.forEach(section => {
        section.documents.forEach(document => {
          if (document._isNewlyAdded) {
            delete document._isNewlyAdded;
          }
          if (document._markedForDeletion) {
            delete document._markedForDeletion;
          }
        });
      });
    }

    // New function to update local state from server response
    async function updateLocalStateFromServerResponse(responseData, operations) {
      // Process the response to update local document IDs
      if (responseData.created_items) {
        responseData.created_items.forEach((createdItem, index) => {
          // Find the corresponding operation and update the local document
          const createOperation = operations.find(op => op.action === 'create');
          if (createOperation) {
            // Find the document in our local state and update its ID
            state.sections.forEach(section => {
              if (section.key === createOperation.section_key) {
                section.documents.forEach(doc => {
                  if (doc._isNewlyAdded && 
                      ((createOperation.document_type_id && doc.document_type_id === createOperation.document_type_id) ||
                       (createOperation.custom_document_name && doc.name === createOperation.custom_document_name))) {
                    doc.clientItemId = createdItem.id;
                    doc.id = `client-custom-${createdItem.id}`;
                  }
                });
              }
            });
          }
        });
      }
      
      // Remove documents that were marked for deletion
      state.sections.forEach(section => {
        section.documents = section.documents.filter(doc => !doc._markedForDeletion);
      });
    }

    // Template and client actions
    async function saveAsTemplate() {
      // Implementation for saving current state as a new template
      console.log('Save as template - to be implemented');
    }

    async function sendToClient() {
      // Implementation for sending checklist to client
      console.log('Send to client - to be implemented');
    }

    // Helper functions
    const getDocumentSuggestions = (sectionKey) => {
      const suggestions = {
        personal_information: ['Driver\'s License', 'Social Security Card', 'Birth Certificate'],
        financial_statements: ['Bank Statement', 'Credit Card Statement', 'Investment Statement'],
        tax_documents: ['W-2 Form', '1099 Form', 'Tax Return'],
        insurance: ['Life Insurance Policy', 'Auto Insurance', 'Health Insurance Card'],
        estate_planning: ['Will', 'Trust Documents', 'Power of Attorney'],
        real_estate: ['Property Deed', 'Mortgage Statement', 'Property Tax Statement'],
      };
      
      return suggestions[sectionKey] || ['Document', 'Statement', 'Certificate'];
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString();
    };

    const getHeaders = () => {
      const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value || 
                      window?.$root?.$data?.csrfToken || 
                      document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'); 

      return {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken || ''
      };
    };

    // Lifecycle
    onMounted(async () => {
      await fetchClientChecklist();
    });

    onUnmounted(() => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    });

    return {
      // State
      state,
      entityName,
      expandedSections,
    
    // Methods
    isExpanded,
    isSystemSection,
    shouldShowSectionContent,
    toggleSection,
    selectTemplate,
    enterSetupMode,
    exitSetupMode,
    applySelectedTemplate,
    updateSectionIncluded,
    updateDocumentRequired,
    addDocumentFromTypeahead,
    removeDocument,
    clearAllDocuments,
    saveAsTemplate,
    sendToClient,
    getDocumentSuggestions,
    formatDate,
  };
}
};
</script>

<style scoped>
.document-checklist-container {
  background-color: #fff;
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.02), 0px 0px 1px 0px rgba(0, 0, 0, 0.02);
  border-radius: 0.75rem;
  padding: 1.5rem;
  max-width: 64rem;
  margin: 0 auto;
}

.header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
}

.template-dropdown {
  min-width: 140px;
}

.setup-form-container {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 2rem;
}

.template-selection-container {
  /* Remove grid layout since we're using Stack component */
}

.template-option-card {
  @apply tw-relative tw-flex tw-items-start tw-gap-4 tw-p-4 tw-border-2 tw-border-slate-200 tw-rounded-lg tw-cursor-pointer tw-transition-all tw-bg-white;
  min-height: 100px;
}

.template-option-card:hover,
.template-option-card:focus {
  @apply tw-border-blue-300 tw-bg-blue-50 tw-shadow-sm tw-outline-none;
}

.template-option-card:focus-visible {
  @apply tw-ring-2 tw-ring-blue-500 tw-ring-offset-2;
}

.template-option-card.template-selected {
  @apply tw-border-blue-500 tw-bg-blue-50 tw-shadow-md;
}

.selection-indicator {
  @apply tw-flex-shrink-0 tw-mt-1;
}

.selection-circle {
  @apply tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-border-slate-300 tw-flex tw-items-center tw-justify-center tw-transition-all;
  background-color: white;
  transform: scale(1);
}

.selection-circle.selected {
  @apply tw-border-blue-500 tw-bg-blue-500;
  transform: scale(1.1);
}

.check-icon {
  @apply tw-text-white;
}

.template-content {
  @apply tw-flex-1 tw-min-w-0;
}

.hover-overlay {
  @apply tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-blue-500 tw-bg-opacity-10 tw-opacity-0 tw-transition-opacity tw-rounded-lg;
  pointer-events: none;
}

.template-option-card:hover .hover-overlay {
  @apply tw-opacity-100;
}

.template-option-card.template-selected .hover-overlay {
  @apply tw-opacity-0;
}

.hover-icon {
  @apply tw-text-blue-600 tw-text-lg;
}

/* Dropdown template selection styles */
.dropdown-template-item {
  @apply tw-cursor-pointer tw-rounded tw-px-3 tw-py-2 tw-transition-all;
}

.dropdown-template-item:hover {
  @apply tw-bg-slate-50;
}

.dropdown-template-selected {
  @apply tw-bg-blue-50 tw-border-l-2 tw-border-blue-500;
}

.dropdown-selection-indicator {
  @apply tw-flex-shrink-0;
}

.dropdown-selection-circle {
  @apply tw-w-4 tw-h-4 tw-rounded-full tw-border-2 tw-border-slate-300 tw-flex tw-items-center tw-justify-center tw-transition-all;
  background-color: white;
  transform: scale(1);
}

.dropdown-selection-circle.selected {
  @apply tw-border-blue-500 tw-bg-blue-500;
  transform: scale(1.05);
}

.dropdown-check-icon {
  @apply tw-text-white;
}

.checklist-content {
  background-color: #fbfcfd;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
}

.section-item {
  @apply tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-p-4 tw-transition-all;
}

.section-item:hover {
  @apply tw-border-slate-300 tw-shadow-sm;
}

.section-disabled {
  @apply tw-opacity-60;
}

.section-header {
  @apply tw-flex tw-items-start tw-justify-between tw-mb-3;
}

.section-content {
  @apply tw-pl-8;
}

.document-item {
  @apply tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-slate-50 tw-border tw-border-slate-100 tw-rounded-lg tw-transition-all;
}

.document-item:hover {
  @apply tw-bg-slate-100 tw-border-slate-200;
}

.document-disabled {
  @apply tw-opacity-60;
}

.document-icon {
  @apply tw-flex-shrink-0 tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-rounded tw-bg-blue-100;
}

.add-document-area {
  @apply tw-mt-3;
}

.empty-section-state {
  @apply tw-bg-slate-50 tw-border tw-border-dashed tw-border-slate-300 tw-rounded-lg tw-p-4;
}

.footer-actions {
  @apply tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-lg tw-p-4;
}

.empty-state {
  @apply tw-p-12 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .document-checklist-container {
    padding: 1rem;
    margin: 0.5rem;
  }
  
  .header {
    padding: 1rem;
  }
  
  .footer-actions .tw-flex {
    @apply tw-flex-col tw-gap-3;
  }
  
  .footer-actions .tw-text-sm {
    @apply tw-text-center;
  }
}
</style> 